import { useState, useEffect, useCallback } from 'react';
import { CreditCard, CheckCircle2, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

// Carte "Connecter mes paiements" (Stripe Connect Express) affichée dans le
// dashboard organisateur. Permet de recevoir automatiquement les droits d'entrée
// des exposants. Tant que non connecté, Festin encaisse et reverse manuellement.
// Flag d'activation. Visible en dev (localhost) pour tester ; caché en prod
// tant que Connect n'est pas validé. Pour activer en prod : remplacer par `true`.
const CONNECT_LIVE = import.meta.env.DEV;

export function PayoutConnectCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState<{ connected: boolean; payoutsEnabled: boolean; detailsSubmitted: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!CONNECT_LIVE) { setLoading(false); return; }
    if (!user) { setLoading(false); return; }
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/connect/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await resp.json();
      if (resp.ok) setStatus(j);
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Retour depuis l'onboarding Stripe : on rafraîchit le statut.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'done' || params.get('connect') === 'refresh') {
      loadStatus();
      // Nettoie l'URL.
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadStatus]);

  const startOnboarding = async () => {
    if (!user) return;
    setWorking(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/connect/onboard`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || 'Erreur');
      window.location.href = j.url; // redirection vers l'onboarding Stripe hébergé
    } catch (e: any) {
      toast.error(e.message);
      setWorking(false);
    }
  };

  // Feature non encore activée en prod (Connect en cours de test) → invisible.
  if (!CONNECT_LIVE) return null;

  if (loading) {
    return (
      <div className="mb-8 rounded-2xl border border-white/10 bg-[#121212] p-5 flex items-center gap-3">
        <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
        <span className="text-sm text-gray-500">Vérification de vos paiements…</span>
      </div>
    );
  }

  // Paiements activés ✅
  if (status?.payoutsEnabled) {
    return (
      <div className="mb-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-6 h-6 text-emerald-400" />
        </div>
        <div className="min-w-0">
          <p className="font-black text-white text-sm">Paiements activés</p>
          <p className="text-xs text-gray-400 mt-0.5">Vous recevez automatiquement les droits d'entrée de vos exposants, directement sur votre compte.</p>
        </div>
      </div>
    );
  }

  // KYC en cours (détails soumis mais pas encore validé par Stripe)
  const inReview = status?.detailsSubmitted && !status?.payoutsEnabled;

  return (
    <div className="mb-8 rounded-2xl border border-[#FF5C00]/25 bg-[#FF5C00]/[0.06] p-5 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#FF5C00]/10 flex items-center justify-center shrink-0">
        {inReview ? <AlertCircle className="w-6 h-6 text-[#FF5C00]" /> : <CreditCard className="w-6 h-6 text-[#FF5C00]" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-black text-white text-sm">
          {inReview ? 'Vérification en cours' : 'Recevez vos paiements automatiquement'}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {inReview
            ? 'Vos informations sont en cours de validation par Stripe. Ce statut se mettra à jour automatiquement.'
            : 'Connectez votre compte pour recevoir directement les droits d\'entrée de vos exposants. En attendant, Festin encaisse et vous reverse manuellement.'}
        </p>
      </div>
      <button
        onClick={startOnboarding}
        disabled={working}
        className="shrink-0 flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#ff7a33] disabled:opacity-50 text-white px-5 py-3 rounded-xl font-black text-sm transition-all"
      >
        {working ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {inReview ? 'Reprendre la vérification' : 'Connecter mes paiements'}
        {!working && <ArrowRight className="w-4 h-4" />}
      </button>
    </div>
  );
}
